import React, { useEffect } from "react";

import { DashboardEvent } from "EmployeeDashboard/analytics";
import { bem } from "EmployeeDashboard/utils";
import { IndividualApplicationURL, useGetPoliciesQuery } from "IndividualApplication/api";
import { IndividualApplicationStatus, IndividualDentalPlan, IndividualVisionPlan } from "IndividualApplication/types";
import { EnrolledIndividualPlan } from "IndividualApplication/utils";
import { capitalize } from "lodash";
import { Link } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { Body1, SubBody } from "Cobalt/BodyText";
import { Callout } from "Cobalt/Callout";
import CarrierLogo from "Cobalt/CarrierLogo";
import Header from "Cobalt/Header";
import BodyText from "Common/BodyText";
import { Card, CardSection } from "Common/Card";
import { Div } from "Common/HTML";
import { Body2 } from "Theme/BodyText";
import { LinkButton } from "Theme/Button";

const renewalDateWithin90Days = (renewalDate: Date) => {
  const date = new Date(renewalDate);
  date.setDate(renewalDate.getDate() - 90);
  return new Date() > date;
};

const PlanCard = ({ plan, planType }: { plan: IndividualDentalPlan | IndividualVisionPlan; planType: string }) => (
  <>
    <Body1 pb={4}>Enrolled {capitalize(planType)} Plan</Body1>
    <Card className="flex justify-start items-center">
      <CarrierLogo plan={plan} mr={4} mt={2} />
      <Body2>{plan.display_name}</Body2>
    </Card>
  </>
);

export interface IndividualApplicationEntryPointCardProps {
  individualEligiblePlans: string[];
  individualApplicationsAvailable: string[];
  individualApplicationStatus: IndividualApplicationStatus;
  adminMode?: boolean;
  showNewBadge?: boolean;
  renewalDate?: Date | null | undefined;
}

export const IndividualApplicationEntryPointCard = ({
  individualEligiblePlans,
  adminMode,
  individualApplicationStatus,
  individualApplicationsAvailable,
  showNewBadge = false,
  renewalDate,
}: IndividualApplicationEntryPointCardProps) => {
  const { data: individualPolicies = [] } = useGetPoliciesQuery({});

  useEffect(() => {
    if (individualEligiblePlans.includes("dental")) {
      const trackingEvent: DashboardEvent = ["individual_dental_entrypoint_viewed", { location: "dashboard", role: "employee", adminMode }];
      AnalyticsTracking.trackEvent(...trackingEvent);
    }

    if (individualEligiblePlans.includes("vision")) {
      const trackingEvent: DashboardEvent = ["individual_vision_entrypoint_viewed", { location: "dashboard", role: "employee", adminMode }];
      AnalyticsTracking.trackEvent(...trackingEvent);
    }
  }, [individualEligiblePlans]);

  let buttonText = "Get Started";
  let buttonDisabled = false;

  if (individualApplicationStatus === "partially_completed") {
    buttonText = "Continue Application";
  } else if (individualApplicationStatus === "submitted") {
    buttonText = "Application Pending";
    buttonDisabled = true;
  }

  const enrolledDentalPlan = EnrolledIndividualPlan(individualPolicies, "dental");
  const enrolledVisionPlan = EnrolledIndividualPlan(individualPolicies, "vision");
  const enrolledPlanTypes = [];
  if (enrolledDentalPlan) enrolledPlanTypes.push("dental");
  if (enrolledVisionPlan) enrolledPlanTypes.push("vision");
  return (
    <Card p={4}>
      <CardSection>
        {renewalDate && renewalDateWithin90Days(renewalDate) && enrolledPlanTypes.length > 0 && (
          <Callout type="warning" showIcon>
            <Body2>
              Your company renewal is happening soon. Check with your employer to see if they plan to add a {enrolledPlanTypes.join(" or ")} plan you can enroll
              in.
            </Body2>
          </Callout>
        )}
        {showNewBadge && (
          <Div className={bem("newTag")} mb={4}>
            🎉 New 🎉
          </Div>
        )}
        <Header level={7} mb={4}>
          Individual {individualEligiblePlans.map((type) => capitalize(type)).join(" and ")} Plans
        </Header>
        {(enrolledDentalPlan || enrolledVisionPlan) && (
          <>
            {enrolledDentalPlan && <PlanCard plan={enrolledDentalPlan} planType="dental" />}
            {enrolledVisionPlan && <PlanCard plan={enrolledVisionPlan} planType="vision" />}
          </>
        )}
        {individualApplicationStatus !== "not_available" && (
          <>
            {(enrolledDentalPlan || enrolledVisionPlan) && <Body1 mt={4}>Enroll in {capitalize(individualApplicationsAvailable?.join(" "))} Plans</Body1>}
            <BodyText mt={4}>
              Your employer is not currently offering {individualApplicationsAvailable?.join(" and ")} insurance. Enroll online for yourself and your family.
            </BodyText>
            <LinkButton component={Link} to={IndividualApplicationURL("ee-dashboard")} mt={4} disabled={buttonDisabled}>
              {buttonText}
            </LinkButton>
            <SubBody pt={4} color="muted">
              By clicking &quot;{buttonText},&quot; you agree to SimplyInsured&apos;s Terms of Service and Privacy Policy.
            </SubBody>
          </>
        )}
      </CardSection>
    </Card>
  );
};
